import { Component, inject, Input, OnInit, signal } from '@angular/core';
import { CarNote } from '../../../../../models/car-note';
import { CarNotesService } from '../../../../../services/vehicles/car-notes-service';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs';
import { MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { MenuModule } from 'primeng/menu';
import { ButtonModule } from 'primeng/button';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { AddCarNoteForm } from '../../../../../@shared/forms/vehicles/cars/add-car-note-form/add-car-note-form';
import { UpdateCarNoteForm } from '../../../../../@shared/forms/vehicles/cars/update-car-note-form/update-car-note-form';
import { DeleteCarNoteForm } from '../../../../../@shared/forms/vehicles/cars/delete-car-note-form/delete-car-note-form';
import { Params } from '../../../../../services/utilis/add-params-service';
import { Card } from 'primeng/card';
import { NotePhotos } from '../../../../../@shared/components/note-photos/note-photos';

@Component({
  selector: 'app-car-details-notes',
  imports: [MenuModule, ButtonModule, PaginatorModule, DatePipe, AddCarNoteForm, UpdateCarNoteForm, DeleteCarNoteForm, Card, NotePhotos],
  templateUrl: './car-details-notes.html',
  styleUrl: './car-details-notes.scss'
})
export class CarDetailsNotes implements OnInit {

  @Input() carID: string

  params = [
    "page",
    "size",
    'sortBy',
    'sortDirection',
    'createdFrom',
    'createdTo',
  ]

  modifiedParams: Params = {
    page: "page",
    size: "size",
    sortBy: "sortBy",
    sortDirection: "sortDirection",
    createdFrom: "createdFrom",
    createdTo: "createdTo",
  }

  carNotesList = signal<CarNote[]>([]);
  totalRecords = signal(0);
  first = 0;
  rows = 20;

  selectedCarNote: CarNote
  addCarNoteDialog = false
  updateCarNoteDialog = false
  deleteCarNoteDialog = false

  private carNotesService = inject(CarNotesService)
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute)

  noteMenuItems: MenuItem[] = [];

  openNoteMenu(event: MouseEvent, note: CarNote, menu: Menu): void {
    this.noteMenuItems = [
      {
        label: 'Edit',
        icon: 'pi pi-pencil',
        command: () => this.openUpdateCarNoteForm(note),
      },
      {
        label: 'Delete',
        icon: 'pi pi-trash',
        styleClass: 'note-menu-danger',
        command: () => this.openDeleteCarNoteForm(note),
      },
    ];
    menu.toggle(event);
  }

  private readonly avatarColors = [
    '#4f7cdc', '#e05c5c', '#5cb85c', '#f0ad4e',
    '#9b59b6', '#1abc9c', '#e67e22', '#3498db',
  ];

  getInitials(name: string): string {
    if (!name) return '?';
    return name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase();
  }

  getAvatarColor(name: string): string {
    if (!name) return this.avatarColors[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return this.avatarColors[Math.abs(hash) % this.avatarColors.length];
  }


  ngOnInit() {
    this.getCarNotesList()
  }

  getCarNotesList = () => {
    this.activatedRoute.queryParams.pipe(

      // ✅ ONLY run when tab = 2
      filter(params => params['tab'] === '2'),

      map((params) =>
        Object.fromEntries(
          Object.entries(params)
            .filter(([key]) => this.params.includes(key))
            .map(([key, value]) => [this.modifiedParams[key] || key, value])
        )
      ),

      distinctUntilChanged((prev, curr) =>
        JSON.stringify(prev) === JSON.stringify(curr)
      ),

      tap((queryParams) => {
        this.rows = queryParams['size'] ? +queryParams['size'] : 20;
      }),

      switchMap((queryParams) => {
        const params = {
          ...queryParams,
          sortBy: queryParams['sortBy'] ?? 'updatedAt',
          sortDirection: queryParams['sortDirection'] ?? 'DESC',
        };
        return this.carNotesService.getCarNotesList(this.carID!, params);
      })

    )
      .subscribe((res) => {
        this.carNotesList.set(res.data.content);
        this.totalRecords.set(res.data.totalElements);
        this.first = res.data.currentPage * res.data.pageSize;
      });
  }

  onPageChange = (event: PaginatorState) => {
    this.router.navigate([], {
      queryParams: { page: event.page, size: event.rows },
      queryParamsHandling: 'merge',
    });
  }

  onCarNoteAdded = () => {
    this.getCarNotesList()
    this.addCarNoteDialog = false
  }

  openUpdateCarNoteForm = (selectedRow: CarNote) => {
    this.selectedCarNote = selectedRow
    this.updateCarNoteDialog = true
  }

  onCarNoteUpdated = () => {
    this.getCarNotesList()
    this.updateCarNoteDialog = false
  }

  openDeleteCarNoteForm = (selectedRow: CarNote) => {
    this.selectedCarNote = selectedRow
    this.deleteCarNoteDialog = true
  }

  onCarNoteDeleted = () => {
    this.getCarNotesList()
    this.deleteCarNoteDialog = false
  }

}
